import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { IconButton } from '@atlaskit/button/new';
import RetryIcon from '@atlaskit/icon/core/retry';
import { Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { usePublish } from '@atlaskit/rovo-triggers';

import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';
import { fg } from '@confluence/feature-gating';

import type { SmartSpaceOverviewData } from '../SmartSpaceOverview.tsx';
import { useRegenerateLoadingState } from '../../useRegenerateLoadingState.ts';

import { QuestionItem } from './QuestionItem.tsx';
import { MOCK_DATA } from './mockData.ts';

export type FAQData = NonNullable<SmartSpaceOverviewData['faq']>;

type FAQEntry = NonNullable<FAQData['entries']>[number];

export type Source = FAQEntry['sources'][number];

type PopularQuestionsProps = {
	faqData: FAQData | null | undefined;
	spaceKey: string;
};

export const PopularQuestions = ({ faqData, spaceKey }: PopularQuestionsProps) => {
	const { formatMessage } = useIntl();
	const [expandedId, setExpandedId] = useState<string | null>(null);
	const [startRefreshAll, isRefreshingAll] = useRegenerateLoadingState();
	const publish = usePublish('ai-mate');

	const data = fg('smart-space-overview-faq-use-backend-query')
		? faqData
		: MOCK_DATA?.confluence_smartSpaceOverview?.faq;

	const handleToggle = useCallback((id: string) => {
		setExpandedId((prev) => (prev === id ? null : id));
	}, []);

	const formatSourcesContext = useCallback(
		(sources: readonly Source[]) => {
			if (sources.length === 0) {
				return '';
			}
			const sourceLines = sources.map(
				(source) =>
					`- [${source?.content?.title}](${window.location.origin}${CONTEXT_PATH}${source?.content?.links?.webui})`,
			);
			return `\n\n${formatMessage(i18n.sourcesLabel)}\n${sourceLines.join('\n')}`;
		},
		[formatMessage],
	);

	const createSpaceContext = useCallback(
		(parentQuestion: FAQEntry) => {
			const spaceOverviewUrl = `${window.location.origin}${SPACE_OVERVIEW.toUrl({ spaceKey })}`;

			const humanMessageContent = formatMessage(i18n.humanMessagePrompt, {
				link: spaceOverviewUrl,
				question: parentQuestion.question,
			});
			const agentMessageContent = `${parentQuestion.answer}${formatSourcesContext(parentQuestion.sources)}`;

			return {
				human_message: {
					content: humanMessageContent,
				},
				agent_message: {
					content: agentMessageContent,
				},
			};
		},
		[spaceKey, formatSourcesContext, formatMessage],
	);

	const publishRovoChat = useCallback(
		(questionText: string, parentQuestion: FAQEntry) => {
			const context = createSpaceContext(parentQuestion);

			publish({
				type: 'chat-new',
				source: 'space-overview-popular-questions',
				data: {
					name: questionText.substring(0, 30),
					dialogues: [context],
					prompt: questionText,
				},
			});
		},
		[publish, createSpaceContext],
	);

	const handleFollowUp = useCallback(
		(followUpQuestion: string, parentQuestion: FAQEntry) =>
			publishRovoChat(followUpQuestion, parentQuestion),
		[publishRovoChat],
	);

	const handleAskRovo = useCallback(
		(customQuestion: string, parentQuestion: FAQEntry) =>
			publishRovoChat(customQuestion, parentQuestion),
		[publishRovoChat],
	);

	const handleRefreshAll = useCallback(() => {
		// TODO: Implement refresh all questions
		setExpandedId(null);
		startRefreshAll();
	}, [startRefreshAll]);

	if ((data?.entries?.length ?? 0) > 0) {
		return (
			<section aria-label={formatMessage(i18n.heading)}>
				<Stack space="space.150">
					<div css={styles.headingRow}>
						<Heading size="small" as="h2">
							{formatMessage(i18n.heading)}
						</Heading>
						{fg('smart-space-overview-refresh') && (
							<div
								css={[styles.refreshAllButton, isRefreshingAll && styles.refreshAllButtonVisible]}
							>
								<IconButton
									icon={RetryIcon}
									label={formatMessage(i18n.refreshAll)}
									appearance="subtle"
									spacing="compact"
									isLoading={isRefreshingAll}
									onClick={handleRefreshAll}
									isTooltipDisabled={false}
								/>
							</div>
						)}
					</div>
					<div css={[isRefreshingAll && styles.contentBlurred]}>
						<Stack space="space.150">
							{data?.entries?.map((question, index) => {
								const questionKey = question.question;
								return (
									<QuestionItem
										key={questionKey}
										question={question}
										isExpanded={expandedId === questionKey}
										onToggle={handleToggle}
										onFollowUp={handleFollowUp}
										onAskRovo={handleAskRovo}
										spaceKey={spaceKey}
										questionPosition={index}
									/>
								);
							})}
						</Stack>
					</div>
				</Stack>
			</section>
		);
	}

	return null;
};

const i18n = defineMessages({
	heading: {
		id: 'space-overview.smart-space-overview.popular-questions.heading',
		defaultMessage: 'Popular questions',
		description: 'Heading for the popular questions section on smart space overview.',
	},
	humanMessagePrompt: {
		id: 'space-overview.smart-space-overview.popular-questions.human-message-prompt',
		defaultMessage: 'Question about space {link}: {question}',
		description: 'The initial human prompt sent to the chat when a popular question is selected.',
	},
	sourcesLabel: {
		id: 'space-overview.smart-space-overview.popular-questions.sources-label',
		defaultMessage: 'Sources:',
		description: 'Label for the sources section in the agent response.',
	},
	refreshAll: {
		id: 'space-overview.smart-space-overview.popular-questions.refresh-all',
		defaultMessage: 'Refresh all questions',
		description: 'Label for the button that refreshes all popular questions.',
	},
});

const styles = cssMap({
	headingRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		'--refresh-all-opacity': '0',
		'&:hover': {
			// @ts-expect-error -- CSS custom property not supported by cssMap types
			'--refresh-all-opacity': '1',
		},
	},
	refreshAllButton: {
		transition: 'opacity 150ms ease-in',
		// @ts-expect-error -- CSS custom property not supported by cssMap types
		opacity: 'var(--refresh-all-opacity)',
	},
	refreshAllButtonVisible: {
		opacity: '1',
	},
	contentBlurred: {
		opacity: token('opacity.disabled'),
		transition: 'opacity 150ms ease-in',
		pointerEvents: 'none',
	},
});
